import { memo, useEffect, useMemo, useState } from "react";
import { Text, Toggle } from "@merge-api/merge-javascript-shared";
import {
  BlueprintRunnerExecutionResponse,
  StepLoggingViewEnum,
} from "../../../../models/Blueprints";
import IntegrationEditorStepLogsTable from "../../../shared/integration-editor-base/IntegrationEditorStepLogsTable";
import LeftPanelSectionHeader from "../shared/LeftPanelSectionHeader";
import LeftPanelSelect from "../shared/LeftPanelSelect";
import StepIOLogsTable from "./step-io-logs/StepIOLogsTable";
import useBlueprintContext from "../../context/useBlueprintContext";

interface Props {
  blueprintRunnerExecutionResponse: BlueprintRunnerExecutionResponse | null;
}

/**
 * Renders the logs and log view options for a Blueprint run
 * If no Blueprint run results, does not render
 */
const BlueprintLeftPanelConsoleResultLogs = ({ blueprintRunnerExecutionResponse }: Props) => {
  const { setStepLoggingView } = useBlueprintContext();

  // Use local state for view toggling to prevent excessive context updates
  const [localView, setLocalView] = useState<StepLoggingViewEnum>(StepLoggingViewEnum.STEP_IO);

  // Toggle to display logs in reverse-chronological order, users routinely need to get to the last entry
  const [reverseLogOrder, setReverseLogOrder] = useState(false);

  // Initialize logging view to step I/O as default
  useEffect(() => {
    setStepLoggingView(StepLoggingViewEnum.STEP_IO);
  }, []);

  // Handle local view change and propagate to context
  const handleViewChange = (_: any, value: StepLoggingViewEnum) => {
    setLocalView(value);
    setStepLoggingView(value);
  };

  // Extract response data safely - these will be undefined if no response
  const exit_data = blueprintRunnerExecutionResponse?.exit_data;
  const start_time = blueprintRunnerExecutionResponse?.start_time || "";

  // Determine if we should show step IO logs
  const isStepIOView = [
    StepLoggingViewEnum.STEP_IO,
    StepLoggingViewEnum.STEP_IO_IN_CANVAS,
  ].includes(localView);

  // Memoize Step logs component - will be a placeholder if no data or early return
  const stepIOLogsComponent = useMemo(() => {
    if (!exit_data?.debugging_info?.step_io_logs) {
      return <Text>No step I/O logs found</Text>;
    }
    return <StepIOLogsTable stepIOLogs={exit_data.debugging_info.step_io_logs} />;
  }, [exit_data?.debugging_info?.step_io_logs]);

  const stepLogsComponent = useMemo(() => {
    if (!exit_data?.step_logs) {
      return <Text>No step logs found</Text>;
    }
    return (
      <IntegrationEditorStepLogsTable
        startTime={start_time}
        stepLogs={exit_data.step_logs}
        reverse={reverseLogOrder}
      />
    );
  }, [exit_data?.step_logs, start_time, reverseLogOrder]);

  if (!blueprintRunnerExecutionResponse) {
    return null;
  }

  return (
    <>
      <hr className="bg-gray-10 p-0 m-0" />
      <div className="flex flex-col space-y-4 py-4">
        <LeftPanelSectionHeader title="Logs" />
        <LeftPanelSelect
          title="Log view"
          placeholder="Default"
          onChange={handleViewChange}
          value={localView}
          options={Object.values(StepLoggingViewEnum)}
          clearable={false}
        />

        {exit_data && (
          <>
            {/* Conditionally render only one type of logs */}
            {isStepIOView ? (
              <div className="flex flex-col space-y-2">
                <Text variant="h6">Step I/O logs</Text>
                {stepIOLogsComponent}
              </div>
            ) : (
              <div className="flex flex-col space-y-2">
                <div className="flex flex-row justify-between">
                  <Text variant="h6">Step logs</Text>
                  <Toggle
                    label={<Text variant="sm">Reverse</Text>}
                    labelPlacement="left"
                    checked={reverseLogOrder}
                    onChange={() => setReverseLogOrder((prev) => !prev)}
                  />
                </div>
                {stepLogsComponent}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default memo(BlueprintLeftPanelConsoleResultLogs);
